import { JobSeekingStatus } from "@nimble/core-client/types";
import { Check, ChevronDown, MessagesSquare, Search, X } from "lucide-react";
import * as React from "react";

import ButtonLoader from "@/components/shared/button-loader";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { profilePreferencesTestIds } from "@/lib/data-test-ids/profile.data-test-ids";
import { cn } from "@/lib/utils";

export type JobSeekingStatusOption = {
  value: JobSeekingStatus;
  label: string;
  shortLabel: string;
  Icon: React.ComponentType<{ className?: string }>;
  description: string;
  dataTestId: string;
};

export const jobSeekingStatusOptions: Record<
  JobSeekingStatus,
  JobSeekingStatusOption
> = {
  [JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES]: {
    value: JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES,
    label: "Open to matches",
    shortLabel: "Open to matches",
    Icon: (props) => (
      <MessagesSquare
        {...props}
        className={cn(props.className, "text-primary dark:text-primary-400")}
      />
    ),
    description:
      "You get weekly recommendations and will be recommended to principals. Principals might reach out directly to you.",
    dataTestId: profilePreferencesTestIds.JOB_SEEKING_STATUS_OPEN_TO_MATCHES,
  },
  [JobSeekingStatus.OPEN_TO_WORK]: {
    value: JobSeekingStatus.OPEN_TO_WORK,
    label: "Open to work",
    shortLabel: "Open to work",
    Icon: (props) => (
      <Search
        {...props}
        className={cn(props.className, "text-info dark:text-info")}
      />
    ),
    description:
      "You're not open to being matched and contacted by principals, but are open to job opportunities.",
    dataTestId: profilePreferencesTestIds.JOB_SEEKING_STATUS_OPEN_TO_WORK,
  },
  [JobSeekingStatus.CLOSED_TO_WORK]: {
    value: JobSeekingStatus.CLOSED_TO_WORK,
    label: "Closed to work",
    shortLabel: "Closed to work",
    Icon: (props) => (
      <X
        {...props}
        className={cn(
          props.className,
          "text-destructive dark:text-destructive",
        )}
      />
    ),
    description: "You're not looking for a new job at this time.",
    dataTestId: profilePreferencesTestIds.JOB_SEEKING_STATUS_CLOSED_TO_WORK,
  },
};

export type TriggerProps = React.ComponentPropsWithoutRef<
  typeof ButtonLoader
> & {
  icon: React.ReactNode;
  label: React.ReactNode;
};

export const MobileTrigger = React.forwardRef<HTMLButtonElement, TriggerProps>(
  ({ label, ...props }, ref) => {
    return (
      <Trigger
        ref={ref}
        {...props}
        label={<span className="hidden md:block">{label}</span>}
        className={cn(
          props.className,
          "[&>div>div:first-child]:gap-0 md:[&>div>div:first-child]:gap-2",
          "[&>div>div:first-child]:justify-center md:[&>div>div:first-child]:justify-start",
        )}
      />
    );
  },
);

MobileTrigger.displayName = "MobileTrigger";

export const Trigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof ButtonLoader> & {
    icon: React.ReactNode;
    label: React.ReactNode;
  }
>(({ icon, label, ...props }, ref) => {
  return (
    <ButtonLoader
      ref={ref}
      {...props}
      variant="outline"
      role="combobox"
      className={cn("w-full px-3", props.className)}
    >
      <div className="flex justify-between w-full items-center min-w-0">
        <div className="flex gap-2 items-center min-w-0 flex-1">
          <div className="flex-shrink-0">{icon}</div>
          <div className="truncate">{label}</div>
        </div>
        <ChevronDown
          className={cn("opacity-50 flex-shrink-0 ml-2", "hidden md:block")}
        />
      </div>
    </ButtonLoader>
  );
});

Trigger.displayName = "Trigger";

export function JobseekingStatusItem({
  onChange,
  option,
  value,
  dataTestId,
  disabled,
}: {
  onChange?: (value: JobSeekingStatus) => void;
  option: JobSeekingStatusOption;
  value?: JobSeekingStatus;
  dataTestId?: string;
  disabled?: boolean;
}) {
  const isSelected = value === option.value;

  return (
    <button
      key={option.value}
      disabled={disabled}
      onClick={() => onChange?.(option.value)}
      className={cn(
        "w-[calc(100%-8px)] px-3 py-2.5 mx-1 my-0.5 text-left rounded-md transition-colors",
        "disabled:pointer-events-none disabled:opacity-50",
        isSelected
          ? "bg-surface-subtle"
          : "bg-transparent hover:bg-surface-subtle",
      )}
      data-testid={dataTestId}
    >
      <div className="flex gap-2.5 w-full items-center text-sm">
        <div className="flex items-center justify-center w-4 h-4 flex-shrink-0">
          <option.Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{option.label}</p>
          <span className="text-xs text-ink-subtle leading-relaxed block">
            {option.description}
          </span>
        </div>
        {isSelected && (
          <Check className="w-4 h-4 text-foreground ml-auto flex-shrink-0" />
        )}
      </div>
    </button>
  );
}

export type TriggerableComponentPropsType = {
  triggerButton: React.ReactNode;
  content: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function JobseekingStatusPopover({
  triggerButton,
  content,
  isOpen,
  onOpenChange,
}: TriggerableComponentPropsType) {
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent className="w-80 p-0 shadow-sm border-border">
        {content}
      </PopoverContent>
    </Popover>
  );
}

export function JobseekingStatusDrawer({
  triggerButton,
  content,
  isOpen,
  onOpenChange,
}: TriggerableComponentPropsType) {
  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
      <DrawerContent>
        <DrawerTitle className="sr-only">Job Search Status</DrawerTitle>
        <div className="mt-4 border-t">{content}</div>
      </DrawerContent>
    </Drawer>
  );
}
